import { createContext, useContext, useMemo, useRef, useState, type ReactNode } from 'react';
import { isoDate } from '../../lib/dates';
import { OwnerBookingModal } from '../owners/OwnerBookingModal';
import { StaffModal } from '../staff/StaffModal';
import { useStaff } from '../staff/useStaff';

// Open an EXISTING record's modal from anywhere in the shell — the capability
// the global search needs and the app didn't have (only users had a global
// opener, on AccountModalProvider). Mirrors that pattern: one host at the top of
// the shell, any component calls open*(id), the same master-detail modal opens
// over the current page. The modals self-hydrate from the id and auto-signal the
// mutation bus on write (lib/api), so onChanged is a no-op — open pages
// revalidate themselves.
//
// Type → modal (Freddi 2026-07-09): booking → edit booking; owner → edit
// household; dog → edit its household (there is no standalone dog modal); helper
// → edit helper. Users stay on useAccountModal().openUserEdit(email).
// Where the modal was opened FROM, so it can offer a way back there. Global
// search passes this so a booking opened from a result can return to the
// results — with the query, scope and scroll intact, because the panel is
// parked rather than unmounted. Absent ⇒ the modal has nothing behind it and
// shows no back control.
export interface RecordModalNav {
  // Label for the back control, e.g. "Results".
  backLabel: string;
  // Return to the origin: closes the modal and revives whatever was parked.
  onBack: () => void;
  // Dismiss everything — the modal AND the origin behind it.
  onDismiss: () => void;
}

/** Which self-service card to open unfolded — a notification bubble's card (Freddi, 2026-10-14). */
export interface RecordFocus {
  card: 'money' | 'vax';
  dogId: string | null;
}

interface RecordModalApi {
  openOwner: (ownerId: string, nav?: RecordModalNav, focus?: RecordFocus | null) => void;
  openBooking: (bookingId: string, nav?: RecordModalNav, focus?: RecordFocus | null) => void;
  openHelper: (helperId: string, nav?: RecordModalNav) => void;
  // Straight into the helper's leave view. `holidayId` opens ONTO one entry —
  // the activity feed knows which holiday changed, but not the record itself.
  openHelperLeave: (helperId: string, holidayId?: string, nav?: RecordModalNav) => void;
}

const Ctx = createContext<RecordModalApi | null>(null);

// null outside the provider (e.g. the staff-only shell) — callers guard on it.
export function useRecordModal(): RecordModalApi | null {
  return useContext(Ctx);
}

type Open =
  | { kind: 'owner'; ownerId: string; focus?: RecordFocus | null }
  | { kind: 'booking'; bookingId: string; focus?: RecordFocus | null }
  | { kind: 'helper'; helperId: string }
  | { kind: 'helperLeave'; helperId: string; holidayId?: string }
  | null;

export function RecordModalProvider({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState<Open>(null);
  // A ref, not state: the nav is set in the same tick as the open and read on
  // render, and making it state would re-render the whole shell twice per open.
  const navRef = useRef<RecordModalNav | null>(null);
  const api = useMemo<RecordModalApi>(
    () => ({
      openOwner: (ownerId, nav, focus) => { navRef.current = nav ?? null; setOpen({ kind: 'owner', ownerId, focus }); },
      openBooking: (bookingId, nav, focus) => { navRef.current = nav ?? null; setOpen({ kind: 'booking', bookingId, focus }); },
      openHelper: (helperId, nav) => { navRef.current = nav ?? null; setOpen({ kind: 'helper', helperId }); },
      openHelperLeave: (helperId, holidayId, nav) => {
        navRef.current = nav ?? null;
        setOpen({ kind: 'helperLeave', helperId, holidayId });
      },
    }),
    [],
  );
  // Dismiss takes the origin with it: closing a booking outright should not
  // leave a parked search sheet behind the page.
  const close = () => {
    const nav = navRef.current;
    navRef.current = null;
    setOpen(null);
    nav?.onDismiss();
  };
  // Back returns to whatever opened this, leaving that origin as it was found.
  const back = () => {
    const nav = navRef.current;
    navRef.current = null;
    setOpen(null);
    nav?.onBack();
  };
  const nav = navRef.current;
  const today = isoDate(new Date());
  const noop = () => {};

  return (
    <Ctx.Provider value={api}>
      {children}
      {open?.kind === 'owner' && (
        <OwnerBookingModal
          key={`owner:${open.ownerId}:${open.focus ? `${open.focus.card}:${open.focus.dogId ?? ''}` : ''}`}
          ownerId={open.ownerId}
          focus={open.focus ?? null}
          initialMode="ownerEdit"
          today={today}
          onClose={close}
          onChanged={noop}
          onBack={nav ? back : undefined}
          backLabel={nav?.backLabel}
        />
      )}
      {open?.kind === 'booking' && (
        <OwnerBookingModal
          key={`booking:${open.bookingId}:${open.focus ? `${open.focus.card}:${open.focus.dogId ?? ''}` : ''}`}
          bookingId={open.bookingId}
          focus={open.focus ?? null}
          initialMode="booking"
          today={today}
          onClose={close}
          onChanged={noop}
          onBack={nav ? back : undefined}
          backLabel={nav?.backLabel}
        />
      )}
      {(open?.kind === 'helper' || open?.kind === 'helperLeave') && (
        <HelperHost
          helperId={open.helperId}
          holidayId={open.kind === 'helperLeave' ? open.holidayId : undefined}
          today={today}
          onClose={close}
          leave={open.kind === 'helperLeave'}
          onBack={nav?.onBack ? back : undefined}
          backLabel={nav?.backLabel}
        />
      )}
    </Ctx.Provider>
  );
}

// StaffModal needs the (small, unpaginated) staff list — fetched lazily, only
// while a helper is being opened, so a session that never opens one pays nothing.
// `leave` opens straight into the helper's leave view (a leave-row click) rather
// than the default helper-edit pane.
function HelperHost({
  helperId,
  holidayId,
  today,
  onClose,
  leave,
  onBack,
  backLabel,
}: {
  helperId: string;
  holidayId?: string;
  today: string;
  onClose: () => void;
  leave?: boolean;
  // Search stores a nav for every record kind, and this branch was the one that
  // never read it — which the always-present back arrow turned from an absence
  // into a DEAD control on a journey that plainly has something behind it.
  onBack?: () => void;
  backLabel?: string;
}) {
  const { state } = useStaff();
  if (state.phase !== 'ready') return null; // opens the moment the list lands
  return (
    <StaffModal
      staff={state.staff}
      helperId={helperId}
      holidayId={holidayId}
      initialMode={leave ? 'leave' : undefined}
      today={today}
      onClose={onClose}
      onChanged={() => {}}
      onBack={onBack}
      backLabel={backLabel}
    />
  );
}
